import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WhatsappFAB from "@/components/WhatsappFAB";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import TawkChat from "@/components/TawkChat";
import SEO from "@/components/SEO";
import useReveal from "@/hooks/useReveal";
import { useI18n } from "@/context/LangContext";
import {
  Handshake,
  Storefront,
  Buildings,
  CurrencyCircleDollar,
  MapPin,
  ArrowRight,
  Check,
  PaperPlaneTilt,
  WhatsappLogo,
} from "@phosphor-icons/react";

const TRACK_KEYS = [
  { icon: "cash", share: "10–15%", key: "track1", perks: [
    "One-page NDA + partner agreement",
    "Deal registration in a shared pipeline",
    "Co-branded proposal + demo deck",
    "Payouts on invoice collection",
  ]},
  { icon: "shop", share: "25–40%", key: "track2", perks: [
    "Full white-label of product UI + docs",
    "Dedicated sandbox tenant per client",
    "Second-line technical support from Maxine",
    "Roadmap influence via quarterly review",
  ]},
  { icon: "building", share: "40–55%", key: "track3", perks: [
    "Exclusive territory (country or region)",
    "Local hosting and support playbook",
    "Joint go-to-market plan & co-funded events",
    "First right on new industry SKUs",
  ]},
];

const REGIONS_OPEN = [
  { flag: "🇳🇬", name: "Nigeria" },
  { flag: "🇰🇪", name: "Kenya" },
  { flag: "🇿🇦", name: "South Africa" },
  { flag: "🇧🇼", name: "Botswana" },
  { flag: "🇿🇲", name: "Zambia" },
  { flag: "🇿🇼", name: "Zimbabwe" },
  { flag: "🇸🇦", name: "Saudi Arabia" },
  { flag: "🇴🇲", name: "Oman" },
  { flag: "🇩🇪", name: "Germany" },
  { flag: "🇫🇷", name: "France" },
  { flag: "🇬🇧", name: "United Kingdom" },
];

const STEP_KEYS = [
  "Submit application (5-min form below)",
  "30-min discovery call within 2 business days",
  "Sign a mutual NDA + partner agreement",
  "Onboarding: sandbox, demo deck, deal-reg portal",
  "First deal registered → first payout on collection",
];

export default function Partners() {
  const { t } = useI18n();
  const ref = useReveal();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    country: "",
    track: "referral",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error(t("partners.form.error"));
      return;
    }
    setSending(true);
    try {
      await api.post("/leads", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        message: `[PARTNER · ${form.track.toUpperCase()} · ${form.country || "unspecified"}] ${form.message}`,
        source: "partner",
      });
      toast.success(t("partners.form.success"));
      setForm({ name: "", email: "", phone: "", company: "", country: "", track: "referral", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={t("partners.seo.title")}
        description={t("partners.seo.desc")}
      />
      <Nav />
      <main data-testid="partners-page" ref={ref} className="reveal pt-24 md:pt-32 pb-16">
        {/* Hero */}
        <section className="border-b border-[color:var(--line)]">
          <div className="container-x py-10 md:py-16 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <span className="w-6 h-px bg-[color:var(--brand)]" />
                <div className="eyebrow">{t("partners.eyebrow")}</div>
              </div>
              <h1 className="mt-4 font-display font-black text-4xl md:text-6xl leading-[1.02] tracking-tight">
                {t("partners.hero.title.a")} <br className="hidden md:block" />
                {t("partners.hero.title.b")}<span className="text-[color:var(--brand)]">{t("partners.hero.title.c")}</span>.
              </h1>
              <p className="mt-5 md:mt-6 text-base md:text-xl text-neutral-700 max-w-2xl leading-relaxed">
                {t("partners.hero.sub")}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="#partner-apply" data-testid="partner-hero-apply" className="btn-brand text-sm">
                  {t("partners.hero.apply")} <ArrowRight size={14} weight="bold" />
                </a>
                <a
                  href="https://wa.me/7054986771?text=Hi%20Maxine%2C%20I%27d%20like%20to%20discuss%20the%20partner%20program."
                  target="_blank"
                  rel="noreferrer"
                  data-testid="partner-hero-wa"
                  className="btn-ghost text-sm"
                >
                  <WhatsappLogo size={14} weight="fill" /> {t("partners.hero.ask")}
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-[color:var(--ink)] p-6 bg-[color:var(--paper-alt)]">
                <div className="eyebrow">{t("partners.territories.eyebrow")}</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {REGIONS_OPEN.map((r) => (
                    <span
                      key={r.name}
                      data-testid={`partner-region-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="inline-flex items-center gap-1.5 border border-[color:var(--line)] bg-white px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest text-neutral-700"
                    >
                      <span aria-hidden>{r.flag}</span> {r.name}
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-sm text-neutral-600">
                  {t("partners.territories.note")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Tracks */}
        <section className="section border-b border-[color:var(--line)]">
          <div className="container-x">
            <div className="max-w-3xl mb-10">
              <div className="eyebrow">{t("partners.tracks.eyebrow")}</div>
              <h2 className="mt-3 font-display font-black text-3xl md:text-5xl leading-[1.02] tracking-tight">
                {t("partners.tracks.title")}
              </h2>
              <p className="mt-4 text-neutral-700 leading-relaxed">
                {t("partners.tracks.note")}
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {TRACK_KEYS.map((tk) => {
                const I = tk.icon === "cash" ? CurrencyCircleDollar : tk.icon === "shop" ? Storefront : Buildings;
                return (
                  <div
                    key={tk.key}
                    data-testid={`partner-track-${tk.key}`}
                    className="border border-[color:var(--line)] hover:border-[color:var(--brand)] transition-colors p-6 flex flex-col bg-white"
                  >
                    <div className="w-11 h-11 bg-[color:var(--brand)] text-white flex items-center justify-center">
                      <I size={20} weight="bold" />
                    </div>
                    <div className="mt-5 eyebrow">{t(`partners.${tk.key}.tagline`)}</div>
                    <h3 className="mt-2 font-display font-bold text-2xl leading-tight">{t(`partners.${tk.key}.title`)}</h3>
                    <div className="mt-4 flex items-baseline gap-2">
                      <span className="font-display font-black text-3xl text-[color:var(--brand)]">{tk.share}</span>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{t(`partners.${tk.key}.period`)}</span>
                    </div>
                    <ul className="mt-5 space-y-2.5 text-sm flex-1">
                      {tk.perks.map((p) => (
                        <li key={p} className="flex gap-2 items-start text-neutral-700">
                          <Check size={14} weight="bold" className="text-[color:var(--brand)] mt-1 shrink-0" /> {p}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-5 pt-4 border-t border-[color:var(--line)]">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{t("partners.bestfor")}</div>
                      <div className="mt-1 text-sm text-neutral-800">{t(`partners.${tk.key}.ideal`)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="section border-b border-[color:var(--line)] bg-[color:var(--paper-alt)]">
          <div className="container-x grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow">{t("partners.steps.eyebrow")}</div>
              <h2 className="mt-3 font-display font-black text-3xl md:text-4xl leading-[1.02] tracking-tight">
                {t("partners.steps.title")}
              </h2>
              <p className="mt-4 text-neutral-700 leading-relaxed">
                {t("partners.steps.note")}
              </p>
            </div>
            <ol className="lg:col-span-7 space-y-4">
              {STEP_KEYS.map((s, i) => (
                <li
                  key={s}
                  data-testid={`partner-step-${i + 1}`}
                  className="flex gap-4 items-start border border-[color:var(--line)] p-4 bg-white"
                >
                  <span className="w-8 h-8 bg-[color:var(--brand)] text-white font-mono text-xs flex items-center justify-center shrink-0">
                    0{i + 1}
                  </span>
                  <span className="font-display text-base md:text-lg text-neutral-800">{s}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Application form */}
        <section id="partner-apply" className="section">
          <div className="container-x grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3">
                <Handshake size={20} weight="bold" className="text-[color:var(--brand)]" />
                <div className="eyebrow">{t("partners.form.eyebrow")}</div>
              </div>
              <h2 className="mt-4 font-display font-black text-3xl md:text-5xl leading-[1.02] tracking-tight">
                {t("partners.form.title")}
              </h2>
              <p className="mt-4 text-neutral-700 leading-relaxed max-w-md">
                {t("partners.form.sub")}
              </p>
              <div className="mt-8 space-y-3 font-mono text-xs uppercase tracking-widest text-neutral-600">
                <div className="flex items-center gap-2"><MapPin size={12} weight="bold" /> Dubai · Bengaluru · Frankfurt · London</div>
                <div>Email · <a href="mailto:support@maxineconsulting.in" className="text-[color:var(--brand)]">support@maxineconsulting.in</a></div>
                <div>WhatsApp · <a href="https://wa.me/7054986771" target="_blank" rel="noreferrer" className="text-[color:var(--brand)]">+91 70549 86771</a></div>
              </div>
            </div>

            <form
              onSubmit={submit}
              data-testid="partner-form"
              className="lg:col-span-7 border border-[color:var(--line)] p-6 md:p-10 bg-white"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label={t("partners.form.name")} testid="partner-name" value={form.name} onChange={handle("name")} required />
                <Field label={t("partners.form.email")} type="email" testid="partner-email" value={form.email} onChange={handle("email")} required />
                <Field label={t("partners.form.company")} testid="partner-company" value={form.company} onChange={handle("company")} required />
                <Field label={t("partners.form.country")} testid="partner-country" value={form.country} onChange={handle("country")} />
                <Field label={t("partners.form.phone")} testid="partner-phone" value={form.phone} onChange={handle("phone")} />
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{t("partners.form.track")}</span>
                  <select
                    data-testid="partner-track"
                    value={form.track}
                    onChange={handle("track")}
                    className="mt-2 w-full bg-white border border-[color:var(--line)] focus:border-[color:var(--brand)] outline-none px-4 py-3"
                  >
                    <option value="referral">{t("partners.track1.title")} (10–15%)</option>
                    <option value="reseller">{t("partners.track2.title")} (25–40%)</option>
                    <option value="distributor">{t("partners.track3.title")} (40–55%)</option>
                  </select>
                </label>
              </div>
              <div className="mt-5">
                <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{t("partners.form.msg")}</label>
                <textarea
                  data-testid="partner-message"
                  rows={4}
                  value={form.message}
                  onChange={handle("message")}
                  className="mt-2 w-full bg-white border border-[color:var(--line)] focus:border-[color:var(--brand)] outline-none px-4 py-3 placeholder:text-neutral-400"
                  placeholder={t("partners.form.msg.ph")}
                />
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="partner-submit"
                  className="btn-brand disabled:opacity-60"
                >
                  <PaperPlaneTilt size={16} weight="bold" /> {sending ? t("partners.form.sending") : t("partners.form.submit")}
                </button>
                <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                  {t("partners.form.sla")}
                </span>
              </div>
            </form>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsappFAB />
      <MobileStickyCTA />
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="mt-2 w-full bg-white border border-[color:var(--line)] focus:border-[color:var(--brand)] outline-none px-4 py-3 placeholder:text-neutral-400"
      />
    </label>
  );
}
